import { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Table, Alert, Button } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getDispatchData,
  getTotalDispatchPending,
  getTotalOrderCompletedToday,
  getTotalOrderPlacedToday,
  getTotalPendingPicklist,
  getTotalPicklistInCheckerPanel,
  getPickerPackerStats,
  getWhatsAppLoginStatus,
} from "../../services/dashboard.service";
import { getCompany } from "../../services/company.service";
import useLocalStorage from "../../hooks/useLocalStorage";
import { formatIndianAmount, formatIndianInteger } from "../../utils/numberFormat";

type StatsRangeKey = "CURRENT_MONTH" | "LAST_MONTH" | "LAST_7_DAYS" | "LAST_30_DAYS" | "CUSTOM";

const getRangeDates = (rangeKey: StatsRangeKey, customRange: [Date | null, Date | null]) => {
  const today = new Date();

  if (rangeKey === "CURRENT_MONTH") {
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { startDate, endDate, label: "Current Month" };
  }

  if (rangeKey === "LAST_MONTH") {
    const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate, endDate, label: "Last Month" };
  }

  if (rangeKey === "LAST_7_DAYS") {
    const endDate = new Date(today);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 6);
    return { startDate, endDate, label: "Last 7 Days" };
  }

  if (rangeKey === "LAST_30_DAYS") {
    const endDate = new Date(today);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    return { startDate, endDate, label: "Last 30 Days" };
  }

  const [startDate, endDate] = customRange;
  return {
    startDate,
    endDate,
    label:
      startDate && endDate
        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
        : "Custom Range",
  };
};

function StatsTable({ title, data }: any) {
  const vorHeaderStyle = {
    background: "#ede9fe",
    color: "#5b21b6",
  };
  const stdHeaderStyle = {
    background: "#dcfce7",
    color: "#166534",
  };
  const vorCellStyle = {
    background: "#f5f3ff",
    color: "#5b21b6",
    fontWeight: 600,
  };
  const stdCellStyle = {
    background: "#f0fdf4",
    color: "#166534",
    fontWeight: 600,
  };
  const formatCount = (value: number | string | null | undefined) => formatIndianInteger(value) || "0";
  const formatAmount = (value: number | string | null | undefined) => formatIndianAmount(value) || "0.00";

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        height: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0.75rem 1rem",
          borderBottom: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span
            style={{
              width: 4,
              height: 18,
              background: "#4f46e5",
              borderRadius: 2,
              display: "inline-block",
            }}
          />
          <span
            style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#1f2937",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {title}
          </span>
        </div>
      </div>
      <Table className='mb-0' style={{ fontSize: "0.78rem" }} bordered responsive>
        <thead>
          <tr
            style={{
              background: "#f9fafb",
              borderBottom: "2px solid #e5e7eb",
              fontSize: "0.72rem",
              textTransform: "uppercase",
            }}
          >
            <th rowSpan={2} className='text-center align-middle border-bottom-0'>
              Name
            </th>
            <th rowSpan={2} className='text-center align-middle border-bottom-0'>
              No. of Picklist
            </th>
            <th colSpan={2} className='text-center' style={vorHeaderStyle}>
              VOR
            </th>
            <th colSpan={2} className='text-center' style={stdHeaderStyle}>
              STD
            </th>
          </tr>
          <tr
            style={{
              background: "#f9fafb",
              fontSize: "0.72rem",
              textTransform: "uppercase",
            }}
          >
            <th className='text-center' style={vorHeaderStyle}>
              Line Item
            </th>
            <th className='text-center' style={vorHeaderStyle}>
              Value
            </th>
            <th className='text-center' style={stdHeaderStyle}>
              Line Item
            </th>
            <th className='text-center' style={stdHeaderStyle}>
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={6} className='text-center p-3 text-muted'>
                No records found
              </td>
            </tr>
          ) : (
            data.map((item: any, index: number) => (
              <tr key={index}>
                <td style={{ fontWeight: 600 }}>{item.name}</td>
                <td className='text-center'>{formatCount(item.pickListCount ?? 0)}</td>
                <td className='text-center' style={vorCellStyle}>
                  {formatCount(item.vorTrueLineItems)}
                </td>
                <td className='text-center' style={vorCellStyle}>
                  {formatAmount(item.vorTrueValue)}
                </td>
                <td className='text-center' style={stdCellStyle}>
                  {formatCount(item.vorFalseLineItems)}
                </td>
                <td className='text-center' style={stdCellStyle}>
                  {formatAmount(item.vorFalseValue)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Card>
  );
}

function CommonCard({ title, count, value }: any) {
  return (
    <div
      style={{
        background: "#f5f7ff",
        border: "1px solid #dbe4ff",
        borderRadius: "8px",
        padding: "1rem 1.1rem",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        borderLeft: "4px solid #4f46e5",
      }}
    >
      <p
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          margin: 0,
        }}
      >
        {title}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginTop: "0.6rem",
        }}
      >
        <div>
          <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginBottom: "2px" }}>Count</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#1f2937", lineHeight: 1 }}>
            {formatIndianInteger(count) || "0"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginBottom: "2px" }}>Value</div>
          <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#4f46e5", lineHeight: 1 }}>
            {formatIndianAmount(value) || "0.00"}
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardSummaryTable({
  title,
  recordCount,
  columns,
  data,
  emptyMessage,
}: {
  title: string;
  recordCount: number;
  columns: { key: string; label: string; width?: string }[];
  data: Record<string, any>[];
  emptyMessage: string;
}) {
  const formatCellValue = (value: any) => {
    if (typeof value === "number") {
      return formatIndianInteger(value);
    }

    return value;
  };

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.75rem 1rem",
          borderBottom: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span
            style={{
              width: 4,
              height: 18,
              background: "#4f46e5",
              borderRadius: 2,
              display: "inline-block",
            }}
          />
          <span
            style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#1f2937",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {title}
          </span>
        </div>
        <span
          style={{
            fontSize: "0.72rem",
            background: "#ede9fe",
            color: "#4f46e5",
            fontWeight: 600,
            padding: "2px 10px",
            borderRadius: 20,
          }}
        >
          {formatIndianInteger(recordCount) || "0"} records
        </span>
      </div>

      <Table className='mb-0' style={{ fontSize: "0.78rem" }}>
        <thead>
          <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
            <th
              style={{
                padding: "0.55rem 1rem",
                fontWeight: 700,
                color: "#374151",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                border: 0,
                width: "40px",
              }}
            >
              #
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  padding: "0.55rem 1rem",
                  fontWeight: 700,
                  color: "#374151",
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  border: 0,
                  width: column.width,
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + 1}
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#9ca3af",
                  fontSize: "0.8rem",
                  border: 0,
                }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={`${title}-${index}`}
                style={{
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <td
                  style={{
                    padding: "0.5rem 1rem",
                    color: "#9ca3af",
                    border: 0,
                    fontWeight: 500,
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#e5e7eb",
                  }}
                >
                  {index + 1}
                </td>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: "0.5rem 1rem",
                      color: "#1f2937",
                      border: 0,
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#e5e7eb",
                    }}
                  >
                    {formatCellValue(row[column.key])}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Card>
  );
}

export default function Home() {
  const [totalOrderPlacedToday, setTotalOrderPlacedToday] = useState({ count: 0, value: 0 });
  const [totalOrderCompletedToday, setTotalOrderCompletedToday] = useState({ count: 0, value: 0 });
  const [totalPendingPicklist, setTotalPendingPicklist] = useState({ count: 0, value: 0 });
  const [picklistInCheckerPanel, setPicklistInCheckerPanel] = useState({ count: 0, value: 0 });
  const [totalDispatchPending, setTotalDispatchPending] = useState([]);
  const [dispatchData, setDispatchData] = useState([]);
  const [pickerStats, setPickerStats] = useState([]);
  const [packerStats, setPackerStats] = useState([]);
  const [statsRangeKey, setStatsRangeKey] = useState<StatsRangeKey>("CURRENT_MONTH");
  const [customRange, setCustomRange] = useState<[Date | null, Date | null]>([null, null]);
  const [statsRangeLabel, setStatsRangeLabel] = useState("Current Month");
  const [company, setCompany] = useLocalStorage<any>("company");
  const [whatsAppStatus, setWhatsAppStatus] = useState({
    isLoggedIn: false,
    mobileNumber: "",
    qrCodeUrl: "",
    isLoading: true,
    hasError: false,
    errorMessage: "",
  });
  const dispatchDataRows = dispatchData.map((dispatch: any) => ({
    date: (
      <span
        style={{
          background: "#f3f4f6",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: "0.75rem",
        }}
      >
        {dispatch.date}
      </span>
    ),
    count: (
      <span style={{ fontWeight: 700, color: "#4f46e5" }}>
        {formatIndianInteger(dispatch.count) || "0"}
      </span>
    ),
    box: <span style={{ fontWeight: 600 }}>{formatIndianInteger(dispatch.box ?? 0) || "0"}</span>,
  }));
  const totalDispatchPendingRows = totalDispatchPending.map((dispatchPending: any) => ({
    date: (
      <span
        style={{
          background: "#f3f4f6",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: "0.75rem",
        }}
      >
        {dispatchPending.date}
      </span>
    ),
    count: (
      <span style={{ fontWeight: 700, color: "#4f46e5" }}>
        {formatIndianInteger(dispatchPending.count) || "0"}
      </span>
    ),
    box: <span style={{ fontWeight: 600 }}>{formatIndianInteger(dispatchPending.box) || "0"}</span>,
  }));

  const fetchWhatsAppStatus = () => {
    setWhatsAppStatus((prev) => ({ ...prev, isLoading: true, hasError: false }));
    getWhatsAppLoginStatus()
      .then((response) => {
        if (response.data.status === "success") {
          // User is logged in
          setWhatsAppStatus({
            isLoggedIn: true,
            mobileNumber: response.data.data.connectedNumeber || "",
            qrCodeUrl: "",
            isLoading: false,
            hasError: false,
            errorMessage: "",
          });
        } else if (response.data.status === "qrcode") {
          // User needs to scan QR code
          setWhatsAppStatus({
            isLoggedIn: false,
            mobileNumber: "",
            qrCodeUrl: response.data.data,
            isLoading: false,
            hasError: false,
            errorMessage: "",
          });
        } else if (response.data.status === "error") {
          // API returned an error status
          setWhatsAppStatus({
            isLoggedIn: false,
            mobileNumber: "",
            qrCodeUrl: "",
            isLoading: false,
            hasError: true,
            errorMessage:
              response.data.message || "Unknown error occurred with WhatsApp connection",
          });
        }
      })
      .catch((error) => {
        setWhatsAppStatus({
          isLoggedIn: false,
          mobileNumber: "",
          qrCodeUrl: "",
          isLoading: false,
          hasError: true,
          errorMessage: error.response?.data?.message || "Failed to connect to WhatsApp service",
        });
      });
  };

  const fetchPickerPackerStats = () => {
    const { startDate, endDate, label } = getRangeDates(statsRangeKey, customRange);

    if (!startDate || !endDate) {
      return;
    }

    setStatsRangeLabel(label);
    getPickerPackerStats({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }).then((response) => {
      setPickerStats(response.data.pickerStats || []);
      setPackerStats(response.data.packerStats || []);
    });
  };

  useEffect(() => {
    // Refresh company data to ensure we have the latest settings
    if (company && company.id) {
      getCompany(company.id)
        .then((response) => {
          const updatedCompany = response.data;
          setCompany(updatedCompany); // Update company data in localStorage
        })
        .catch(() => {
          // Refresh failed; keep existing company data.
        });
    }

    getTotalOrderPlacedToday().then((response) => {
      setTotalOrderPlacedToday(response.data);
    });
    getTotalOrderCompletedToday().then((response) => {
      setTotalOrderCompletedToday(response.data);
    });
    getTotalPendingPicklist().then((response) => {
      setTotalPendingPicklist(response.data);
    });
    getTotalPicklistInCheckerPanel().then((response) => {
      setPicklistInCheckerPanel(response.data);
    });
    getTotalDispatchPending().then((response) => {
      setTotalDispatchPending(response.data);
    });
    getDispatchData().then((response) => {
      setDispatchData(response.data);
    });
    // Initial fetch of WhatsApp status
    fetchWhatsAppStatus();
  }, []);

  useEffect(() => {
    fetchPickerPackerStats();
  }, [statsRangeKey, customRange]);

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Dashboard</h1>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <CommonCard
            title='Total order placed today'
            count={totalOrderPlacedToday.count}
            value={totalOrderPlacedToday.value}
          />
        </Col>
        <Col md={3}>
          <CommonCard
            title='Total order completed today'
            count={totalOrderCompletedToday.count}
            value={totalOrderCompletedToday.value}
          />
        </Col>
        <Col md={3}>
          <CommonCard
            title='Total pending picklist'
            count={totalPendingPicklist.count}
            value={totalPendingPicklist.value}
          />
        </Col>
        <Col md={3}>
          <CommonCard
            title='Total picklist pending for invoice'
            count={picklistInCheckerPanel.count}
            value={picklistInCheckerPanel.value}
          />
        </Col>

        <Col md={6} className='mt-3'>
          <DashboardSummaryTable
            title='Total Dispatch Pending Data'
            recordCount={totalDispatchPending.length}
            columns={[
              { key: "date", label: "Date" },
              { key: "count", label: "Count" },
              { key: "box", label: "Box" },
            ]}
            data={totalDispatchPendingRows}
            emptyMessage='No pending dispatch data'
          />
        </Col>

        <Col md={6} className='mt-3'>
          <DashboardSummaryTable
            title='Dispatch Data'
            recordCount={dispatchDataRows.length}
            columns={[
              { key: "date", label: "Date" },
              { key: "count", label: "Count" },
              { key: "box", label: "Box" },
            ]}
            data={dispatchDataRows}
            emptyMessage='No dispatch data'
          />
        </Col>

        <Col md={12} className='mt-3'>
          <Card
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <CardBody className='d-flex flex-column gap-2'>
              <div className='d-flex flex-wrap gap-2'>
                <Button
                  size='sm'
                  color={statsRangeKey === "CURRENT_MONTH" ? "primary" : "light"}
                  onClick={() => setStatsRangeKey("CURRENT_MONTH")}
                >
                  Current Month
                </Button>
                <Button
                  size='sm'
                  color={statsRangeKey === "LAST_MONTH" ? "primary" : "light"}
                  onClick={() => setStatsRangeKey("LAST_MONTH")}
                >
                  Last Month
                </Button>
                {/* <Button
                  size='sm'
                  color={statsRangeKey === "LAST_7_DAYS" ? "primary" : "light"}
                  onClick={() => setStatsRangeKey("LAST_7_DAYS")}
                >
                  Last 7 Days
                </Button>
                <Button
                  size='sm'
                  color={statsRangeKey === "LAST_30_DAYS" ? "primary" : "light"}
                  onClick={() => setStatsRangeKey("LAST_30_DAYS")}
                >
                  Last 30 Days
                </Button> */}
                {/* <Button
                  size='sm'
                  color={statsRangeKey === "CUSTOM" ? "primary" : "light"}
                  onClick={() => setStatsRangeKey("CUSTOM")}
                >
                  Custom Range
                </Button> */}
              </div>

              {statsRangeKey === "CUSTOM" && (
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <span className='text-muted' style={{ fontSize: "0.82rem" }}>
                    Select date range:
                  </span>
                  <DatePicker
                    selectsRange
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                    onChange={(dates: [Date | null, Date | null]) => setCustomRange(dates)}
                    isClearable
                    dateFormat='dd/MM/yyyy'
                    className='form-control form-control-sm'
                    placeholderText='Select date range'
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={6} className='mt-3'>
          <StatsTable title={`Picker Statistics (${statsRangeLabel})`} data={pickerStats} />
        </Col>
        <Col md={6} className='mt-3'>
          <StatsTable title={`Packer Statistics (${statsRangeLabel})`} data={packerStats} />
        </Col>

        {company.isEnableMessage && (
          <Col md={6} className='mt-3'>
            <p
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: "0.5rem",
              }}
            >
              WhatsApp Status
            </p>
            <Card
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <CardBody>
                <div className='d-flex justify-content-between align-items-center'>
                  {!whatsAppStatus.isLoading && (
                    <Button size='sm' color='primary' onClick={fetchWhatsAppStatus}>
                      Refresh
                    </Button>
                  )}
                </div>
                <hr />

                {whatsAppStatus.isLoading ? (
                  <Alert color='info'>Loading WhatsApp status...</Alert>
                ) : whatsAppStatus.hasError ? (
                  <Alert color='danger'>
                    <div className='d-flex flex-column'>
                      <p className='mb-2'>Error connecting to WhatsApp service:</p>
                      <p className='mb-2'>{whatsAppStatus.errorMessage}</p>
                      <Button
                        color='outline-danger'
                        size='sm'
                        className='mt-2 align-self-end'
                        onClick={fetchWhatsAppStatus}
                      >
                        Try Again
                      </Button>
                    </div>
                  </Alert>
                ) : whatsAppStatus.isLoggedIn ? (
                  <Alert color='success'>
                    <i className='bi bi-check-circle-fill me-2'></i>
                    Already logged in with {whatsAppStatus.mobileNumber}
                  </Alert>
                ) : whatsAppStatus.qrCodeUrl ? (
                  <div className='text-center'>
                    <p className='text-muted mt-2'>
                      <small>Scan this QR code to log in to WhatsApp</small>
                    </p>

                    <img
                      src={whatsAppStatus.qrCodeUrl}
                      alt='WhatsApp Login QR Code'
                      style={{ maxWidth: "200px", margin: "0 auto" }}
                    />
                  </div>
                ) : (
                  <Alert color='warning'>WhatsApp status unknown. Please try refreshing.</Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
